const SUDOWRITE_HANDLE = "@Sudowrite";
const SUDOWRITE_REWRITE_HINT = "rewrite this";
const SUDOWRITE_BOUNDARY_REGEX = /[\s([{"'`]/;

export type SudowriteTabCompletionHintVariant = "mention" | "rewrite";

export interface SudowriteTabCompletionResult {
  value: string;
  caretPosition: number;
  hintVariant: SudowriteTabCompletionHintVariant;
  hintText: string;
}

export function getSudowriteTabCompletion(
  value: string,
  selectionStart: number,
  selectionEnd: number
): SudowriteTabCompletionResult | null {
  if (typeof value !== "string") return null;
  if (typeof selectionStart !== "number" || typeof selectionEnd !== "number") return null;
  if (selectionStart !== selectionEnd) return null;
  if (selectionStart < 0 || selectionStart > value.length) return null;

  const before = value.slice(0, selectionStart);
  const after = value.slice(selectionStart);

  // Preserve native Tab focus navigation for empty input accessibility.
  // Tab completion only activates after the user starts an @mention token.

  // Stage 1: complete partial @s... to @sudowrite.
  const atIndex = before.lastIndexOf("@");
  if (atIndex >= 0) {
    const token = before.slice(atIndex);
    const charBeforeAt = atIndex > 0 ? before[atIndex - 1] : "";
    const tokenLower = token.toLowerCase();
    const handleLower = SUDOWRITE_HANDLE.toLowerCase();
    const hasTrailingTextAfterCursor = /\S/.test(after);
    const isMentionBoundary = !charBeforeAt || SUDOWRITE_BOUNDARY_REGEX.test(charBeforeAt);
    const isAlphaMentionToken = /^@[a-z]*$/i.test(token);

    if (
      isMentionBoundary &&
      isAlphaMentionToken &&
      !hasTrailingTextAfterCursor &&
      (handleLower.startsWith(tokenLower) || tokenLower === handleLower)
    ) {
      const needsTrailingSpace = after.length === 0 || !/^\s/.test(after);
      const completedHandle = `${SUDOWRITE_HANDLE}${needsTrailingSpace ? " " : ""}`;
      const nextValue = `${before.slice(0, atIndex)}${completedHandle}${after}`;
      const caretPosition = before.slice(0, atIndex).length + completedHandle.length;
      const mentionText = SUDOWRITE_HANDLE.slice(1);
      const tokenMentionText = token.slice(1);
      const hintText = mentionText.slice(tokenMentionText.length);
      return { value: nextValue, caretPosition, hintVariant: "mention", hintText };
    }
  }

  // Stage 2: once @sudowrite is present, tab fills the suggested rewrite prompt.
  const mentionMatch = before.match(/(^|[\s([{"'`])@sudowrite\s*$/i);
  if (!mentionMatch) return null;
  if (after.length > 0 && !/^\s*$/.test(after)) return null;

  const mentionPrefix = mentionMatch[1] ?? "";
  const mentionStart = before.length - mentionMatch[0].length + mentionPrefix.length;
  const normalizedAfter = after.replace(/^\s+/, "");
  const rewrittenValue = `${before.slice(0, mentionStart)}${SUDOWRITE_HANDLE} ${SUDOWRITE_REWRITE_HINT} ${normalizedAfter}`;
  const rewrittenCaretPosition = mentionStart + SUDOWRITE_HANDLE.length + 1 + SUDOWRITE_REWRITE_HINT.length + 1;

  return {
    value: rewrittenValue,
    caretPosition: rewrittenCaretPosition,
    hintVariant: "rewrite",
    hintText: SUDOWRITE_REWRITE_HINT,
  };
}
